import { TypographyProps } from '@mui/material';
import { HTMLAttributes } from 'react';
import { makeStyles } from 'tss-react/mui';

import { COLORS } from '@/styles/tokens/colors';
import { WithClasses } from '@/styles/types';

import { TileThemedTypographyClamped } from './TileThemedTypography';
import { TileFontSizeVariant, TileVariant } from './types';

export const ROW_HEIGHT_PX = 24;
export const ROW_PADDING_HORIZONTAL_PX = 16;
export const ROW_PADDING_VERTICAL_PX = 4;

const useStyles = makeStyles()((_theme) => ({
  root: {
    paddingTop: `${ROW_PADDING_VERTICAL_PX}px`,
    paddingBottom: `${ROW_PADDING_VERTICAL_PX}px`,
    paddingLeft: `${ROW_PADDING_HORIZONTAL_PX}px`,
    paddingRight: `${ROW_PADDING_HORIZONTAL_PX}px`,
    display: 'flex',
    gap: '4px',
    '&:hover': {
      backgroundColor: COLORS.FUNCTIONAL.HOVER,
    },
  },
  description: {
    color: COLORS.GRAY[900],
  },
  value: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    overflow: 'visible', // Prevent the value from being clipped by the description when it is clamped
    color: COLORS.TEXT.SECONDARY,
  },
}));

export interface TileGroupItemProps extends HTMLAttributes<HTMLDivElement> {
  classes?: WithClasses<typeof useStyles>;
  typographyProps?: TypographyProps;
  description: string;
  value?: string;
  tileVariant: TileVariant;
  /** A hex color that replaces the palette of `tileVariant`. */
  tileCustomColor?: string;
  fontSizeVariant: TileFontSizeVariant;
}

export const TileGroupItem = ({
  classes: externalClasses,
  typographyProps,
  description,
  value,
  tileVariant,
  tileCustomColor,
  fontSizeVariant,
  ...props
}: TileGroupItemProps): JSX.Element => {
  const { classes } = useStyles(undefined, {
    props: { classes: externalClasses },
  });

  return (
    <div className={classes.root} {...props}>
      <TileThemedTypographyClamped
        variant="body-xs"
        lines={1}
        className={classes.description}
        tileVariant={tileVariant}
        tileCustomColor={tileCustomColor}
        fontSizeVariant={fontSizeVariant}
        {...typographyProps}
      >
        {description}
      </TileThemedTypographyClamped>
      {value && (
        <TileThemedTypographyClamped
          variant="body-xs"
          lines={1}
          className={classes.value}
          tileVariant={tileVariant}
          tileCustomColor={tileCustomColor}
          fontSizeVariant={fontSizeVariant}
          {...typographyProps}
        >
          {value}
        </TileThemedTypographyClamped>
      )}
    </div>
  );
};
